import type { OrganizationMember } from '@kirin/api-client/contracts/organization-members';

/**
 * Role badge component
 * Handles an array of roles, showing the highest priority role
 */
export const RoleBadge = ({
  roles,
}: {
  roles: OrganizationMember['roles'];
}) => {
  const roleConfig = {
    owner: {
      className: 'bg-red-100 text-red-800 border-red-200',
      label: 'Owner',
      priority: 0,
    },
    manager: {
      className: 'bg-blue-100 text-blue-800 border-blue-200',
      label: 'Manager',
      priority: 1,
    },
    member: {
      className: 'bg-gray-100 text-gray-800 border-gray-200',
      label: 'Member',
      priority: 2,
    },
    support: {
      className: 'bg-purple-700 text-white border-purple-400',
      label: 'Support',
      priority: 3,
    },
  };

  // Handle empty roles array - show as warning since this shouldn't happen
  if (!roles || roles.length === 0) {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border bg-yellow-100 text-yellow-800 border-yellow-200">
        No Role
      </span>
    );
  }

  // Find the highest priority role (lowest priority number)
  const highestRole = roles.reduce((highest, role) => {
    const currentConfig = roleConfig[role as keyof typeof roleConfig];
    const highestConfig = roleConfig[highest as keyof typeof roleConfig];

    // If current role is unknown, skip it
    if (!currentConfig) return highest;
    // If highest is unknown, use current
    if (!highestConfig) return role;
    // Compare priorities
    return currentConfig.priority < highestConfig.priority ? role : highest;
  }, roles[0]);

  // Get config or use fallback for unknown roles
  const config = roleConfig[highestRole as keyof typeof roleConfig] ?? {
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    label: highestRole.charAt(0).toUpperCase() + highestRole.slice(1), // Capitalize first letter
    priority: 999,
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${config.className}`}
    >
      {config.label}
    </span>
  );
};
